import axios from 'axios';

// Create axios instance
export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL ,
  // timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and convert data to snake_case
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and convert data to camelCase
api.interceptors.response.use(
  (response) => {
    // Convert response data from snake_case to camelCase
    if (response.data && typeof response.data === 'object') {
      response.data = snakeToCamel(response.data);
      console.log('Converted response data to camelCase:', response.data);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);


// Attendance API
export const attendanceAPI = {
  getOverview: (schoolId, params = {}) => api.get(`/attendance/${schoolId}/overview`, { params }),
  getStudentAttendance: (schoolId, studentId, params = {}) => api.get(`/attendance/${schoolId}/student/${studentId}`, { params }),
  markAttendance: (schoolId, attendanceData) => api.post(`/attendance/${schoolId}/mark`, attendanceData),
  getClassAttendance: (schoolId, classId, sectionId, params = {}) => api.get(`/attendance/${schoolId}/class/${classId}/section/${sectionId}`, { params }),
  getEmployeeAttendance: (schoolId, employeeId, params = {}) => api.get(`/attendance/${schoolId}/employee/${employeeId}`, { params }),
};

// Exams API
export const examsAPI = {
  getAll: (schoolId, params = {}) => api.get(`/exams/${schoolId}`, { params }),
  getById: (schoolId, examId) => api.get(`/exams/${schoolId}/${examId}`),
  create: (schoolId, examData) => api.post(`/exams/${schoolId}`, examData),
  update: (schoolId, examId, examData) => api.put(`/exams/${schoolId}/${examId}`, examData),
  getStudentMarks: (schoolId, studentId, examId) => api.get(`/exams/${schoolId}/student/${studentId}/exam/${examId}`),
  enterMarks: (schoolId, marksData) => api.post(`/exams/${schoolId}/marks`, marksData),
  getSchedules: (schoolId, classId, sectionId, params = {}) => api.get(`/exams/${schoolId}/schedules/class/${classId}/section/${sectionId}`, { params }),
  getStats: (schoolId) => api.get(`/exams/${schoolId}/stats/overview`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: (schoolId) => api.get(`/dashboard/${schoolId}/stats`),
  getActivities: (schoolId) => api.get(`/dashboard/${schoolId}/activities`),
  getAttendanceTrends: (schoolId) => api.get(`/dashboard/${schoolId}/attendance-trends`),
};

// Schools API
export const schoolsAPI = {
  getAll: () => api.get('/schools'),
  getById: (schoolId) => api.get(`/schools/${schoolId}`),
  create: (schoolData) => api.post('/schools', schoolData),
  update: (schoolId, schoolData) => api.put(`/schools/${schoolId}`, schoolData),
  delete: (schoolId) => api.delete(`/schools/${schoolId}`),
};



// Academic Years API
export const academicYearsAPI = {
  getAll: (schoolId) => api.get(`/academic-years/${schoolId}`),
  getCurrent: (schoolId) => api.get(`/academic-years/${schoolId}/current`),
  create: (schoolId, yearData) => api.post(`/academic-years/${schoolId}`, yearData),
  update: (schoolId, yearId, yearData) => api.put(`/academic-years/${schoolId}/${yearId}`, yearData),
  delete: (schoolId, yearId) => api.delete(`/academic-years/${schoolId}/${yearId}`),
};



// Utility functions for case conversion
const toCamelCase = (str) => str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

const convertKeys = (obj, converter) => {
  if (obj === null || typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map(item => convertKeys(item, converter));
  }

  const converted = {};
  for (const [key, value] of Object.entries(obj)) {
    const newKey = converter(key);
    converted[newKey] = convertKeys(value, converter);
  }
  return converted;
};

const snakeToCamel = (obj) => convertKeys(obj, toCamelCase);

// Utility functions
export const apiUtils = {
  // Handle file uploads
  uploadFile: (endpoint, file, additionalData = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });

    return api.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Handle bulk operations
  bulkOperation: (endpoint, operation, data) => {
    return api.post(`${endpoint}/bulk`, { operation, data });
  },

  // Export data
  exportData: (endpoint, format = 'csv', params = {}) => {
    return api.get(`${endpoint}/export`, {
      params: { format, ...params },
      responseType: 'blob'
    });
  },
};

export default api;